import React, {useState, useEffect, useCallback} from 'react';
import {getFilterOptions, getAreaInfo} from '../services/api';

/**
 * Панель фильтров для последовательного выбора: район -> охотпользователь -> охотхозяйство -> охотучасток
 */

const FILTER_CONFIG = {
    users: {
        filterName: 'users',
        parentFilter: 'region',
        childFilters: ['ecos'],
        getParams: (filters) => ({
            region: filters.region
        })
    },
    ecos: {
        filterName: 'ecos',
        parentFilter: 'users',
        childFilters: [],
        getParams: (filters) => ({
            users: filters.users,
            region: filters.region
        })
    },
    lands: {
        filterName: 'lands',
        parentFilter: 'users',
        childFilters: [],
        getParams: (filters) => {
            const params = {
                users: filters.users,
                region: filters.region
            };
            // ecos опционален
            if (filters.ecos) {
                params.ecos = filters.ecos;
            }
            return params;
        }
    }
};

function FilterPanel({apiUrl, filters, onFiltersChange, onAreaSelect, mapRef, layerManagerRef}) {
    const [loading, setLoading] = useState({region: false, users: false, ecos: false, lands: false});
    const [options, setOptions] = useState({region: [], users: [], ecos: [], lands: []});
    const [autoSelected, setAutoSelected] = useState({users: false, ecos: false, lands: false});

    // Общая функция загрузки опций фильтра
    const loadFilterOptions = useCallback(async (filterKey, parentFilters) => {
        const config = FILTER_CONFIG[filterKey];
        if (!config) return;

        setLoading(prev => ({...prev, [filterKey]: true}));
        try {
            const params = config.getParams(parentFilters);
            const data = await getFilterOptions(config.filterName, params, apiUrl);
            setOptions(prev => ({...prev, [filterKey]: data}));
        } catch (error) {
            console.error(`Error loading ${filterKey}:`, error);
            setOptions(prev => ({...prev, [filterKey]: []}));
        } finally {
            setLoading(prev => ({...prev, [filterKey]: false}));
        }
    }, [apiUrl]);

    // Общая функция сброса дочерних фильтров
    const resetChildFilters = useCallback((filterKey) => {
        const config = FILTER_CONFIG[filterKey];
        if (!config) return;

        const childFilters = config.childFilters;
        const resetOptions = {};
        const resetFilters = {};
        const resetAutoSelected = {};

        childFilters.forEach(child => {
            resetOptions[child] = [];
            resetFilters[child] = null;
            resetAutoSelected[child] = false;
        });

        setOptions(prev => ({...prev, ...resetOptions}));
        onFiltersChange(prev => ({...prev, ...resetFilters}));
        setAutoSelected(prev => ({...prev, ...resetAutoSelected}));
    }, [onFiltersChange]);

    // Функция автовыбора единственного элемента
    const handleAutoSelect = useCallback((filterKey, optionsList, currentValue, isLoading) => {
        if (optionsList.length === 1 && !isLoading) {
            const singleOption = optionsList[0];
            if (currentValue !== singleOption.value) {
                onFiltersChange(prev => ({...prev, [filterKey]: singleOption.value}));
                setAutoSelected(prev => ({...prev, [filterKey]: true}));
                return true;
            } else {
                // Если значение уже установлено, проверяем и устанавливаем флаг автовыбора
                setAutoSelected(prev => {
                    if (!prev[filterKey]) {
                        return {...prev, [filterKey]: true};
                    }
                    return prev;
                });
                return true;
            }
        } else if (optionsList.length !== 1) {
            setAutoSelected(prev => {
                if (prev[filterKey]) {
                    return {...prev, [filterKey]: false};
                }
                return prev;
            });
        }
        return false;
    }, [onFiltersChange]);

    // Загрузка районов при монтировании
    useEffect(() => {
        const loadRegions = async () => {
            setLoading(prev => ({...prev, region: true}));
            try {
                const regions = await getFilterOptions('region', {}, apiUrl);
                setOptions(prev => ({...prev, region: regions}));
            } catch (error) {
                console.error('Error loading regions:', error);
            } finally {
                setLoading(prev => ({...prev, region: false}));
            }
        };
        loadRegions();
    }, [apiUrl]);

    // Загрузка охотпользователей при выборе района
    useEffect(() => {
        if (!filters.region) {
            resetChildFilters('users');
            return;
        }
        loadFilterOptions('users', filters);
    }, [filters.region, loadFilterOptions, resetChildFilters]);

    // Автоматический выбор users
    useEffect(() => {
        handleAutoSelect('users', options.users, filters.users, loading.users);
    }, [options.users, filters.users, loading.users, handleAutoSelect]);

    // Загрузка охотхозяйств при выборе охотпользователя
    useEffect(() => {
        if (!filters.users) {
            resetChildFilters('ecos');
            return;
        }
        loadFilterOptions('ecos', filters);
    }, [filters.users, filters.region, loadFilterOptions, resetChildFilters]);

    // Автоматический выбор ecos
    useEffect(() => {
        if (!loading.users && !loading.ecos) {
            handleAutoSelect('ecos', options.ecos, filters.ecos, false);
        }
    }, [options.ecos, filters.ecos, loading.users, loading.ecos, handleAutoSelect]);

    // Загрузка охотучастков при выборе охотпользователя (ecos опционален)
    useEffect(() => {
        if (!filters.users) {
            resetChildFilters('users'); // Сбрасываем lands при отсутствии users
            setOptions(prev => ({...prev, lands: []}));
            onFiltersChange(prev => ({...prev, lands: null}));
            setAutoSelected(prev => ({...prev, lands: false}));
            return;
        }
        loadFilterOptions('lands', filters);
    }, [filters.users, filters.ecos, filters.region, loadFilterOptions, resetChildFilters, onFiltersChange]);

    // Автоматический выбор lands с загрузкой данных, подсветкой и переходом (как при ручном выборе)
    useEffect(() => {
        const applySingleLand = (landId) => {
            const layerManager = layerManagerRef?.current;
            getAreaInfo('landspol', landId, apiUrl)
                .then(areaInfo => {
                    if (areaInfo) {
                        onAreaSelect({
                            id: landId,
                            layerId: 'landspol',
                            ...areaInfo
                        });
                    }
                    if (layerManager) {
                        layerManager.selectFeatures('landspol', landId);
                        layerManager.gotoFeatures('landspol', landId);
                    }
                })
                .catch(error => console.error('Error loading area info:', error));
        };

        if (options.lands.length === 1 && !loading.lands && !loading.users) {
            const singleLand = options.lands[0];
            const landId = singleLand.value;
            if (filters.lands !== landId) {
                onFiltersChange(prev => ({...prev, lands: landId}));
                setAutoSelected(prev => ({...prev, lands: true}));
                applySingleLand(landId);
            } else if (!autoSelected.lands) {
                setAutoSelected(prev => ({...prev, lands: true}));
                applySingleLand(landId);
            }
        } else if (options.lands.length !== 1 && autoSelected.lands) {
            setAutoSelected(prev => ({...prev, lands: false}));
        }
    }, [options.lands, filters.lands, loading.lands, loading.users, onFiltersChange, onAreaSelect, apiUrl, autoSelected.lands]);

    // Обработка выбора участка (с подсветкой и переходом на карте)
    const handleLandSelect = useCallback(async (landId) => {
        const layerManager = layerManagerRef?.current;
        if (!landId) {
            onAreaSelect(null);
            layerManager?.clearSelection();
            return;
        }

        try {
            const areaInfo = await getAreaInfo('landspol', landId, apiUrl);
            if (areaInfo) {
                onAreaSelect({
                    id: landId,
                    layerId: 'landspol',
                    ...areaInfo
                });
            }
            if (layerManager) {
                await layerManager.selectFeatures('landspol', landId);
                await layerManager.gotoFeatures('landspol', landId);
            }
        } catch (error) {
            console.error('Error loading area info:', error);
        }
    }, [apiUrl, onAreaSelect]);

    // Обработчик изменения фильтра с сбросом автовыбора
    const handleFilterChange = useCallback((filterKey, value, isAutoSelected) => {
        const newValue = value || null;
        onFiltersChange(prev => ({...prev, [filterKey]: newValue}));
        if (newValue !== null && isAutoSelected) {
            setAutoSelected(prev => ({...prev, [filterKey]: false}));
        }
    }, [onFiltersChange]);

    // Мемоизированные обработчики для каждого фильтра
    const handleUsersChange = useCallback((e) => {
        const newValue = e.target.value || null;
        onFiltersChange(prev => ({
            ...prev,
            users: newValue,
            ecos: null,
            lands: null
        }));
        if (newValue !== null && autoSelected.users) {
            setAutoSelected(prev => ({...prev, users: false}));
        }
    }, [handleFilterChange, autoSelected.users]);

    const handleEcosChange = useCallback((e) => {
        const newValue = e.target.value || null;
        onFiltersChange(prev => ({
            ...prev,
            ecos: newValue,
            lands: null
        }));
        if (newValue !== null && autoSelected.ecos) {
            setAutoSelected(prev => ({...prev, ecos: false}));
        }
    }, [onFiltersChange, autoSelected.ecos]);

    const handleLandsChange = useCallback((e) => {
        const newValue = e.target.value || null;
        handleFilterChange('lands', newValue, autoSelected.lands);
        handleLandSelect(newValue);
    }, [handleFilterChange, handleLandSelect, autoSelected.lands]);

    // Вычисление disabled состояний
    const disabledStates = {
        users: !filters.region || loading.users || autoSelected.users,
        ecos: !filters.users || loading.users || loading.ecos || autoSelected.ecos,
        lands: !filters.users || loading.users || loading.lands || autoSelected.lands
    };
    // Кнопки подсветки и перехода к выбранному объекту для region и lands реализованы
    return (
        <div className="filter-panel">
            <h3>Фильтры</h3>

            <div className="filter-item">
                <label>Район:</label>
                <div className="filter-controls">
                    <select
                        value={filters.region || ''}
                        onChange={async (e) => {
                            const val = e.target.value || null;
                            // Смена района должна атомарно сбрасывать дочерние фильтры,
                            // чтобы стили пунктов выдачи не применялись к смешанному состоянию.
                            onFiltersChange(prev => ({
                                ...prev,
                                region: val,
                                users: null,
                                ecos: null,
                                lands: null
                            }));
                            setAutoSelected(prev => ({
                                ...prev,
                                users: false,
                                ecos: false,
                                lands: false
                            }));
                            setOptions(prev => ({
                                ...prev,
                                users: [],
                                ecos: [],
                                lands: []
                            }));
                            const layerManager = layerManagerRef?.current;
                            if (val && layerManager) {
                                await layerManager.selectFeatures('admpol', val);
                                await layerManager.gotoFeatures('admpol', val);
                            } else if (layerManager) {
                                layerManager.clearSelection();
                            }
                        }}
                        disabled={loading.region}
                    >
                        <option value="">Выберите район</option>
                        {options.region.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.title}</option>
                        ))}
                    </select>
                    <div className="filter-buttons">
                        <button
                            className="filter-button select-button"
                            title="Подсветить район"
                            disabled={!filters.region || !layerManagerRef?.current}
                            onClick={async () => {
                                const layerManager = layerManagerRef?.current;
                                if (layerManager && filters.region) {
                                    // Принудительно триггерим обновление стиля pickuppnt
                                    // даже при повторном ручном выборе того же района.
                                    onFiltersChange(prev => ({...prev, region: filters.region}));
                                    await layerManager.selectFeatures('admpol', filters.region);
                                }
                            }}
                        >
                            📍
                        </button>
                        <button
                            className="filter-button goto-button"
                            title="Перейти к району"
                            disabled={!filters.region || !layerManagerRef?.current}
                            onClick={async () => {
                                const layerManager = layerManagerRef?.current;
                                if (layerManager && filters.region) {
                                    await layerManager.gotoFeatures('admpol', filters.region);
                                }
                            }}
                        >
                            🔍
                        </button>
                    </div>
                </div>
            </div>

            <div className="filter-item">
                <label>Охотпользователь:</label>
                <select
                    value={filters.users || ''}
                    onChange={handleUsersChange}
                    disabled={disabledStates.users}
                >
                    <option value="">Выберите охотпользователя</option>
                    {options.users.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.title}</option>
                    ))}
                </select>
            </div>

            <div className="filter-item">
                <label>Охотхозяйство:</label>
                <select
                    value={filters.ecos || ''}
                    onChange={handleEcosChange}
                    disabled={disabledStates.ecos}
                >
                    <option value="">Выберите охотхозяйство</option>
                    {options.ecos.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.title}</option>
                    ))}
                </select>
            </div>

            <div className="filter-item">
                <label>Охотучасток:</label>
                <div className="filter-controls">
                    <select
                        value={filters.lands || ''}
                        onChange={handleLandsChange}
                        disabled={disabledStates.lands}
                    >
                        <option value="">Выберите охотучасток</option>
                        {options.lands.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.title}</option>
                        ))}
                    </select>
                    <div className="filter-buttons">
                        <button
                            className="filter-button select-button"
                            title="Подсветить участок"
                            disabled={!filters.lands || !layerManagerRef?.current}
                            onClick={async () => {
                                const layerManager = layerManagerRef?.current;
                                if (layerManager && filters.lands) {
                                    await layerManager.selectFeatures('landspol', filters.lands);
                                }
                            }}
                        >
                            📍
                        </button>
                        <button
                            className="filter-button goto-button"
                            title="Перейти к участку"
                            disabled={!filters.lands || !layerManagerRef?.current}
                            onClick={async () => {
                                const layerManager = layerManagerRef?.current;
                                if (layerManager && filters.lands) {
                                    await layerManager.gotoFeatures('landspol', filters.lands);
                                }
                            }}
                        >
                            🔍
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FilterPanel;
